// Shareable cards — the app's one cheap viral loop. Draw a branded 1080×1350
// image on a canvas and hand it to the native share sheet (mobile), falling back
// to a PNG download. No external assets (system fonts, flat fills) so it always
// renders. Reuses the LINEBRKR palette.
import { getNFLLeaderboard, getNFLResults, getNFLSlate, getNFLSharpestSpots, getNFLRedZoneSpots, getNFLScorers, getNFLDueTds, getNFLMatchupSheets, getNFLDefense } from './api'
import { bookName } from './ui'
import { playText } from './plain'

const C = {
  bg: '#0A0E14', bg2: '#0B1017', card: '#111827', line: '#1E2A3A',
  green: '#00D97E', text: '#F1F5F9', muted: '#A6B2C4', faint: '#5A6675',
}
const W = 1080, H = 1350

function base(ctx) {
  ctx.fillStyle = C.bg
  ctx.fillRect(0, 0, W, H)
  // top green glow
  const g = ctx.createRadialGradient(W / 2, -120, 60, W / 2, -120, 720)
  g.addColorStop(0, 'rgba(0,217,126,0.18)')
  g.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, 700)
  // wordmark
  ctx.textAlign = 'left'
  ctx.font = '900 52px system-ui, -apple-system, Arial'
  ctx.fillStyle = C.text
  ctx.fillText('LINE', 72, 130)
  const lw = ctx.measureText('LINE').width
  ctx.fillStyle = C.green
  ctx.fillText('BRKR', 72 + lw, 130)
  // NFL badge
  const bx = 72 + lw + ctx.measureText('BRKR').width + 20
  ctx.fillStyle = C.green
  roundRect(ctx, bx, 96, 92, 42, 8); ctx.fill()
  ctx.fillStyle = '#04140D'
  ctx.font = '800 26px system-ui, Arial'
  ctx.fillText('NFL', bx + 20, 126)
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function footer(ctx) {
  ctx.textAlign = 'center'
  // Proof line — every shared card carries the one claim competitors can't match.
  ctx.font = '800 26px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText('✓ CLV-PROVEN · CALIBRATED · linebrkr.app', W / 2, H - 108)
  ctx.font = '500 24px system-ui, Arial'
  ctx.fillStyle = C.faint
  ctx.fillText('LINEBRKR · research tool, not betting advice · 21+', W / 2, H - 60)
}

function wrapName(ctx, name, x, y, max, size) {
  ctx.font = `900 ${size}px system-ui, -apple-system, Arial`
  if (ctx.measureText(name).width <= max) { ctx.fillText(name, x, y); return y }
  const parts = name.split(' ')
  let line = '', ly = y
  for (const p of parts) {
    const t = line ? line + ' ' + p : p
    if (ctx.measureText(t).width > max && line) { ctx.fillText(line, x, ly); ly += size + 6; line = p }
    else line = t
  }
  ctx.fillText(line, x, ly)
  return ly
}

// pick: {name, pos, team, opp, market, line, isTD, prob(0-1), edge, book, verdict}
export function drawPickCard(pick) {
  const cv = document.createElement('canvas')
  cv.width = W; cv.height = H
  const ctx = cv.getContext('2d')
  base(ctx)

  const hit = Math.round((pick.prob || 0) * 100)
  ctx.textAlign = 'left'
  // eyebrow
  ctx.font = '800 30px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText((pick.verdict || 'TOP PLAY').toUpperCase(), 72, 300)

  // name (wraps)
  ctx.fillStyle = C.text
  const ny = wrapName(ctx, pick.name || 'Player', 72, 400, W - 144, 88)

  // matchup
  ctx.font = '500 36px system-ui, Arial'
  ctx.fillStyle = C.muted
  ctx.fillText(`${pick.pos || ''} · ${pick.team || ''} ${pick.isTD ? '' : 'vs '}${pick.pos === 'QB' ? '@ ' : pick.isTD ? 'vs ' : ''}${pick.opp || ''}`.replace(/\s+/g, ' ').trim(), 72, ny + 70)

  // bet chip
  const bet = pick.isTD ? 'Anytime TD' : `${playText(pick.market, pick.line)}`
  ctx.font = '700 40px system-ui, Arial'
  const bw = ctx.measureText(bet).width
  ctx.fillStyle = 'rgba(0,217,126,0.12)'
  roundRect(ctx, 72, ny + 110, bw + 56, 76, 16); ctx.fill()
  ctx.strokeStyle = C.green; ctx.lineWidth = 2
  roundRect(ctx, 72, ny + 110, bw + 56, 76, 16); ctx.stroke()
  ctx.fillStyle = C.green
  ctx.fillText(bet, 100, ny + 160)

  // big hit%
  ctx.textAlign = 'center'
  ctx.font = '900 320px system-ui, -apple-system, Arial'
  ctx.fillStyle = C.green
  ctx.fillText(`${hit}%`, W / 2, 1120)
  ctx.font = '700 40px system-ui, Arial'
  ctx.fillStyle = C.muted
  ctx.fillText(pick.isTD ? 'MODEL CHANCE TO SCORE' : 'MODEL CHANCE TO HIT', W / 2, 1180)

  // best-price / edge strip
  if (pick.book || pick.edge != null) {
    const bits = []
    if (pick.book) bits.push(`Best price · ${pick.book}`)
    if (pick.edge != null && Math.abs(pick.edge) >= 0.5) bits.push(`${pick.edge >= 0 ? '+' : ''}${pick.edge.toFixed(1)}% edge`)
    ctx.font = '600 30px system-ui, Arial'
    ctx.fillStyle = C.faint
    ctx.fillText(bits.join('   ·   '), W / 2, 1240)
  }

  footer(ctx)
  return cv
}


// Rich proof card — the screenshot-and-post track record. A headline hit rate plus up to
// four supporting stat tiles (record, recent form, book-line record, beat-the-close), all
// drawn from the real scorecard/weekly-record data. Adaptive: renders only the tiles that
// have data, so it works both preseason (backtested headline alone) and in-season.
// d: { eyebrow, big, bigLabel, context, tiles:[{label,value,good}], shareText }
export function drawProofCard(d) {
  const cv = document.createElement('canvas')
  cv.width = W; cv.height = H
  const ctx = cv.getContext('2d')
  base(ctx)

  ctx.textAlign = 'left'
  ctx.font = '800 30px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText((d.eyebrow || 'TRACK RECORD').toUpperCase(), 72, 300)

  // headline number
  ctx.textAlign = 'center'
  ctx.font = '900 300px system-ui, -apple-system, Arial'
  ctx.fillStyle = C.green
  ctx.fillText(d.big || '—', W / 2, 600)
  ctx.font = '700 44px system-ui, Arial'
  ctx.fillStyle = C.text
  ctx.fillText(d.bigLabel || '', W / 2, 672)
  if (d.context) {
    ctx.font = '500 30px system-ui, Arial'
    ctx.fillStyle = C.muted
    ctx.fillText(d.context, W / 2, 726)
  }

  // up to four supporting stat tiles in a 2×2 grid
  const tiles = (d.tiles || []).slice(0, 4)
  const gx = 72, gw = W - 144, colW = (gw - 32) / 2, rowH = 172, ty0 = 812
  tiles.forEach((t, i) => {
    const col = i % 2, row = Math.floor(i / 2)
    const x = gx + col * (colW + 32), y = ty0 + row * (rowH + 26)
    ctx.fillStyle = C.card
    roundRect(ctx, x, y, colW, rowH, 20); ctx.fill()
    ctx.strokeStyle = C.line; ctx.lineWidth = 2
    roundRect(ctx, x, y, colW, rowH, 20); ctx.stroke()
    ctx.textAlign = 'center'
    ctx.font = '900 74px system-ui, -apple-system, Arial'
    ctx.fillStyle = t.good === false ? C.muted : (t.good ? C.green : C.text)
    ctx.fillText(String(t.value), x + colW / 2, y + 98)
    ctx.font = '600 26px system-ui, Arial'
    ctx.fillStyle = C.muted
    ctx.fillText((t.label || '').toUpperCase(), x + colW / 2, y + 142)
  })

  footer(ctx)
  return cv
}

export function shareProof(d) {
  return shareCanvas(drawProofCard(d), 'linebrkr-proof.png', d.shareText || 'LINEBRKR track record')
}

// Hand a canvas to the native share sheet, else download the PNG.
export function shareCanvas(cv, filename, text) {
  return new Promise(resolve => {
    cv.toBlob(async blob => {
      if (!blob) { resolve(false); return }
      const file = new File([blob], filename, { type: 'image/png' })
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text })
          resolve(true); return
        }
      } catch (_) { /* user cancelled or unsupported → fall through to download */ }
      try {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = filename; a.click()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
      } catch (_) {}
      resolve(true)
    }, 'image/png')
  })
}

export function sharePick(pick) {
  const bet = pick.isTD ? 'Anytime TD' : `${playText(pick.market, pick.line)}`
  return shareCanvas(drawPickCard(pick), 'linebrkr-pick.png',
    `${pick.name} — ${bet} · ${Math.round((pick.prob || 0) * 100)}% on LINEBRKR`)
}


// Result "postcard" — the celebratory graphic when a surfaced play HITS (a TD, a yardage
// over, a receptions over). The MLB app's hit/homer card, for football. Reports a graded
// outcome — no stake, no advice. play: {name, pos, team, opp, market, line, actual, prob(0-1), week}
export function drawResultCard(play) {
  const cv = document.createElement('canvas')
  cv.width = W; cv.height = H
  const ctx = cv.getContext('2d')
  base(ctx)
  const isTD = play.isTD || play.market === 'Anytime TD'
  const pct = play.prob != null ? Math.round((play.prob > 1 ? play.prob / 100 : play.prob) * 100) : null

  ctx.textAlign = 'left'
  ctx.font = '800 30px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText(`✓ HIT${play.week ? ` · WEEK ${play.week}` : ''}`, 72, 300)

  ctx.fillStyle = C.text
  const ny = wrapName(ctx, play.name || 'Player', 72, 400, W - 144, 88)

  ctx.font = '500 36px system-ui, Arial'
  ctx.fillStyle = C.muted
  ctx.fillText(`${play.pos || ''} · ${play.team || ''}${play.opp ? ` vs ${play.opp}` : ''}`.replace(/\s+/g, ' ').trim(), 72, ny + 70)

  // bet chip — green filled since it cashed
  const bet = isTD ? 'Anytime TD' : `${playText(play.market, play.line)}`
  ctx.font = '700 40px system-ui, Arial'
  const bw = ctx.measureText(bet).width
  ctx.fillStyle = 'rgba(0,217,126,0.16)'
  roundRect(ctx, 72, ny + 110, bw + 56, 76, 16); ctx.fill()
  ctx.strokeStyle = C.green; ctx.lineWidth = 2
  roundRect(ctx, 72, ny + 110, bw + 56, 76, 16); ctx.stroke()
  ctx.fillStyle = C.green
  ctx.fillText(bet, 100, ny + 160)

  // big result
  ctx.textAlign = 'center'
  ctx.fillStyle = C.green
  if (isTD) {
    ctx.font = '900 300px system-ui, -apple-system, Arial'
    ctx.fillText('✓', W / 2, 1040)
    ctx.font = '700 56px system-ui, Arial'
    ctx.fillStyle = C.text
    ctx.fillText('TOUCHDOWN', W / 2, 1130)
  } else {
    ctx.font = '900 300px system-ui, -apple-system, Arial'
    ctx.fillText(play.actual != null ? String(play.actual) : '✓', W / 2, 1040)
    ctx.font = '700 44px system-ui, Arial'
    ctx.fillStyle = C.text
    ctx.fillText(`${(play.market || '').toUpperCase()} · CLEARED ${play.line}`, W / 2, 1120)
  }

  if (pct != null) {
    ctx.font = '600 30px system-ui, Arial'
    ctx.fillStyle = C.faint
    ctx.fillText(`Our model had it ${pct}% to hit`, W / 2, 1210)
  }

  footer(ctx)
  return cv
}

export function shareResult(play) {
  const isTD = play.isTD || play.market === 'Anytime TD'
  const bet = isTD ? 'Anytime TD' : `${playText(play.market, play.line)}`
  const res = isTD ? 'scored' : (play.actual != null ? `went ${play.actual}` : 'cleared')
  return shareCanvas(drawResultCard(play), 'linebrkr-hit.png',
    `✓ ${play.name} ${res} — ${bet} hit on LINEBRKR`)
}

// ── Board postcards (the ranked-slate share graphics) ────────────────────────
// The NFL answer to the MLB "Top 10 Board / Yard Work / Power Spots" cards. All are
// drawn on a canvas and handed to shareCanvas (native share sheet → PNG download).
const MONO = 'ui-monospace, "SF Mono", Menlo, monospace'

function _ellip(ctx, text, maxW) {
  text = String(text || '')
  if (ctx.measureText(text).width <= maxW) return text
  while (text.length > 1 && ctx.measureText(text + '…').width > maxW) text = text.slice(0, -1)
  return text + '…'
}
// The % a card headlines on: the model's calibrated hit probability (P over the line),
// falling back to prob_over. Rounded to a whole percent.
const _rowPct = r => Math.round(((r.hit_prob != null ? r.hit_prob : r.prob_over) || 0) * 100)
// Dedupe by player + cap at 2 per team (a whole unit vs one bad D shouldn't own a card),
// dropping rows with no opponent. Shapes rows for the canvas renderers.
function _capNfl(rows, n) {
  const seen = new Set(), tc = {}, out = []
  for (const e of (rows || [])) {
    const nm = e.full_name; if (!nm || seen.has(nm)) continue
    const t = e.team || ''; if (t && (tc[t] || 0) >= 2) continue
    seen.add(nm); tc[t] = (tc[t] || 0) + 1
    out.push({ name: nm, team: t, pos: e.pos || '', vs: e.opp || '',
               pct: _rowPct(e), proj: e.proj, note: e.note || '', value: !!e.value })
    if (out.length === n) break
  }
  return out
}
function _todayStr() {
  return new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
}

// Card Studio — one flexible generator behind the Studio tab. Renders a titled, ranked
// list for ONE game (top scorers, a single market, a game plan, or graded results). The
// caller shapes every row and passes the compliance footer, so the same renderer serves a
// projections card ("calibrated probabilities") and a graded-results card ("graded at
// settlement") without any bet/stake language. Height grows with the row count.
//   row = { name, meta, value, sub, status ('hit'|'miss'|null → ✓/✗ badge), hot (bool) }
export function drawStudioCard({ brandKicker, eyebrow, title, subtitle, rows = [], footer }) {
  const Wd = 1080, PAD = 56, rowH = 118, rowGap = 14, n = rows.length
  const headH = 300, RED = '#ef5a52'
  const Hd = PAD + headH + n * (rowH + rowGap) + 26 + 44 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, 700)
  const x = PAD
  // wordmark + NFL badge
  let y = PAD + 46
  ctx.textAlign = 'left'; ctx.font = '900 44px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  const bx = x + lw + ctx.measureText('BRKR').width + 16
  ctx.fillStyle = C.green; roundRect(ctx, bx, y - 30, 74, 36, 7); ctx.fill()
  ctx.fillStyle = '#04140D'; ctx.font = '800 20px system-ui, Arial'; ctx.fillText('NFL', bx + 14, y - 3)
  if (brandKicker) {
    ctx.textAlign = 'right'; ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint
    ctx.fillText(brandKicker, Wd - PAD, PAD + 40); ctx.textAlign = 'left'
  }
  // eyebrow · title · subtitle
  y += 46; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green; ctx.fillText((eyebrow || '').toUpperCase(), x, y)
  y += 60; ctx.font = '900 72px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(_ellip(ctx, title || '', Wd - 2 * PAD), x, y)
  y += 34; ctx.font = '700 22px system-ui, Arial'; ctx.fillStyle = C.muted; ctx.fillText(_ellip(ctx, subtitle || '', Wd - 2 * PAD), x, y)
  // rows
  y = PAD + headH
  const nameMax = Wd - 2 * PAD - 88 - 260
  rows.forEach((r, i) => {
    const ry = y; y += rowH + rowGap
    const hot = !!r.hot, graded = r.status === 'hit' || r.status === 'miss', good = r.status === 'hit'
    ctx.fillStyle = hot ? 'rgba(0,217,126,0.08)' : C.card
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 18); ctx.fill()
    ctx.strokeStyle = hot ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.5
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 18); ctx.stroke()
    // left marker: a ✓/✗ badge for graded rows, else the rank
    const cy = ry + rowH / 2
    if (graded) {
      const col = good ? C.green : RED
      ctx.fillStyle = good ? 'rgba(0,217,126,0.16)' : 'rgba(239,90,82,0.16)'
      ctx.beginPath(); ctx.arc(x + 46, cy, 23, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(x + 46, cy, 23, 0, Math.PI * 2); ctx.stroke()
      ctx.textAlign = 'center'; ctx.font = '900 26px system-ui, Arial'; ctx.fillStyle = col
      ctx.fillText(good ? '✓' : '✕', x + 46, cy + 9)
    } else {
      ctx.textAlign = 'center'; ctx.font = '800 28px ' + MONO; ctx.fillStyle = hot ? C.green : C.faint
      ctx.fillText(String(i + 1), x + 46, cy + 10)
    }
    // name + meta
    const nx = x + 88
    ctx.textAlign = 'left'; ctx.font = '900 38px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name || '', nameMax), nx, ry + 50)
    ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(_ellip(ctx, r.meta || '', nameMax), nx, ry + 86)
    // value (+ optional sub) on the right
    const vRight = Wd - PAD - 28
    ctx.textAlign = 'right'
    ctx.fillStyle = graded ? (good ? C.green : RED) : (hot ? C.green : C.text)
    ctx.font = '900 46px ' + MONO
    const vy = r.sub ? cy + 4 : cy + 16
    ctx.fillText(_ellip(ctx, String(r.value != null ? r.value : ''), 300), vRight, vy)
    if (r.sub) { ctx.font = '700 16px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(_ellip(ctx, String(r.sub), 300), vRight, vy + 27) }
    ctx.textAlign = 'left'
  })
  y += 26; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText(footer || 'Calibrated model probabilities · research, not advice · 21+', Wd / 2, Hd - PAD - 8)
  ctx.textAlign = 'left'
  return cv
}

// Landscape 4-market Top-10 board (Anytime TD · Rec Yds · Rush Yds · Receptions).
function drawNflTop10({ dateStr, columns }) {
  const Wd = 2000, PAD = 60, GAP = 28
  const headH = 210, colHeadH = 66, rowH = 86, rowGap = 10, footH = 52, nRows = 10
  const Hd = PAD + headH + colHeadH + nRows * (rowH + rowGap) + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.15, -100, 80, Wd * 0.15, -100, 1300)
  glow.addColorStop(0, 'rgba(0,217,126,0.14)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  let x = PAD, y = PAD + 50
  ctx.textAlign = 'left'; ctx.font = '900 46px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.fillStyle = C.green; ctx.font = '800 22px ' + MONO
  ctx.fillText('NFL', x + lw + ctx.measureText('BRKR').width + 14, y)
  y += 82; ctx.font = '900 80px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('TOP 10 ', x, y)
  const tw = ctx.measureText('TOP 10 ').width; ctx.fillStyle = C.green; ctx.fillText('BOARD.', x + tw, y)
  ctx.textAlign = 'right'; ctx.font = '800 22px ' + MONO; ctx.fillStyle = C.green
  ctx.fillText((dateStr || '') + ' · WEEK', Wd - PAD, PAD + 38)
  ctx.font = '700 16px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('Model-ranked · calibrated · four markets', Wd - PAD, PAD + 68)
  ctx.textAlign = 'left'
  const gridY = PAD + headH, colW = (Wd - 2 * PAD - 3 * GAP) / 4
  columns.forEach((col, ci) => {
    const cx = PAD + ci * (colW + GAP)
    ctx.textAlign = 'left'; ctx.font = '900 30px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(col.title, cx + 4, gridY + 30)
    ctx.font = '700 14px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(col.sub || '', cx + 4, gridY + 54)
    let ry = gridY + colHeadH
    for (let i = 0; i < nRows; i++) {
      const r = (col.rows || [])[i], top = i < 3
      ctx.fillStyle = top ? 'rgba(0,217,126,0.07)' : C.card; roundRect(ctx, cx, ry, colW, rowH, 13); ctx.fill()
      ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.2; roundRect(ctx, cx, ry, colW, rowH, 13); ctx.stroke()
      if (r) {
        ctx.textAlign = 'left'
        ctx.font = '700 19px ' + MONO; ctx.fillStyle = top ? C.green : C.faint; ctx.fillText(String(i + 1), cx + 16, ry + rowH / 2 + 7)
        ctx.font = '900 23px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(_ellip(ctx, r.name, colW - 150), cx + 46, ry + 34)
        ctx.font = '700 13px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(_ellip(ctx, `${r.team}${r.vs ? ` · ${r.vs}` : ''}`, colW - 150), cx + 46, ry + 58)
        ctx.textAlign = 'right'; ctx.font = '900 30px ' + MONO; ctx.fillStyle = top ? C.green : C.text; ctx.fillText(`${r.pct}%`, cx + colW - 16, ry + rowH / 2 + 10)
        ctx.textAlign = 'left'
      }
      ry += rowH + rowGap
    }
  })
  ctx.textAlign = 'center'; ctx.font = '600 18px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Top 3 per market highlighted · calibrated model probabilities · research, not advice · 21+', Wd / 2, Hd - PAD - 2)
  ctx.textAlign = 'left'
  return cv
}

// Portrait single-market leaderboard (e.g. Anytime TD).
function drawNflMarket({ title, sub, dateStr, rows }) {
  const Wd = 1080, PAD = 56, rowH = 116, rowGap = 13, n = rows.length
  let Hd = PAD + 52 + 40 + 90 + 34 + 24 + n * (rowH + rowGap) + 26 + 44 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 46
  ctx.textAlign = 'left'; ctx.font = '900 44px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'; ctx.fillText('NFL PROP MODEL', Wd - PAD, PAD + 40); ctx.textAlign = 'left'
  y += 40; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green; ctx.fillText((dateStr || '') + ' · WEEK', x, y)
  y += 64; ctx.font = '900 72px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(title, x, y)
  y += 34; ctx.font = '700 22px system-ui, Arial'; ctx.fillStyle = C.muted; ctx.fillText(sub, x, y)
  y += 24
  rows.forEach((r, i) => {
    const ry = y; y += rowH + rowGap; const top = i < 2
    ctx.fillStyle = top ? 'rgba(0,217,126,0.08)' : C.card; roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 18); ctx.fill()
    ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.5; roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 18); ctx.stroke()
    ctx.textAlign = 'left'; ctx.font = '900 38px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, Wd - 2 * PAD - 260), x + 28, ry + 52)
    ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(_ellip(ctx, `${r.pos ? r.pos + ' · ' : ''}${r.team}${r.vs ? ` · vs ${r.vs}` : ''}`, Wd - 2 * PAD - 260), x + 28, ry + 88)
    ctx.textAlign = 'right'; ctx.font = '900 56px ' + MONO; ctx.fillStyle = top ? C.green : C.text
    ctx.fillText(`${r.pct}%`, Wd - PAD - 28, ry + rowH / 2 + 18)
    ctx.textAlign = 'left'
  })
  y += 26; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Model-ranked · graded after every slate · research, not advice · 21+', Wd / 2, Hd - PAD - 8)
  ctx.textAlign = 'left'
  return cv
}

// Market → (headline, subhead) for the rankings export.
const _RANK_META = {
  receptions:  { title: 'RECEPTIONS',  sub: 'model-ranked · over the line' },
  anytime_td:  { title: 'ANYTIME TD',  sub: 'model-ranked · to score' },
  rec_yards:   { title: 'REC YARDS',   sub: 'model-ranked · over the line' },
  rush_yards:  { title: 'RUSH YARDS',  sub: 'model-ranked · over the line' },
  pass_yards:  { title: 'PASS YARDS',  sub: 'model-ranked · over the line' },
  pass_tds:    { title: 'PASS TDs',    sub: 'model-ranked · over the line' },
}
const _lineLabel = (market, line) =>
  market === 'anytime_td' ? 'TD' : (line != null ? playText(market, line) : '')

// Portrait player-rankings card — the Players tab, cleanly exported. Rank, player,
// matchup + line, hit%, with a green edge on Value plays (model beats the price) and the
// top 3 highlighted. Height grows with the row count.
function drawNflRankings({ title, sub, dateStr, week, rows }) {
  const Wd = 1080, PAD = 56, rowH = 104, rowGap = 12, n = rows.length
  const Hd = PAD + 46 + 40 + 78 + 30 + 26 + n * (rowH + rowGap) + 20 + 46 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 46
  ctx.textAlign = 'left'; ctx.font = '900 44px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'
  ctx.fillText('NFL PLAYER RANKINGS', Wd - PAD, PAD + 40); ctx.textAlign = 'left'
  y += 40; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green
  ctx.fillText(`${dateStr || ''} · WEEK ${week ?? ''}`.trim(), x, y)
  y += 62; ctx.font = '900 70px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(title, x, y)
  y += 30; ctx.font = '700 22px system-ui, Arial'; ctx.fillStyle = C.muted; ctx.fillText(sub, x, y)
  y += 26
  rows.forEach((r, i) => {
    const ry = y; y += rowH + rowGap; const top = i < 3
    ctx.fillStyle = top ? 'rgba(0,217,126,0.08)' : C.card
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.fill()
    ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.5
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.stroke()
    // Value edge — a green bar on the left rim when the model beats the posted price.
    if (r.value) { ctx.fillStyle = C.green; roundRect(ctx, x, ry + 14, 6, rowH - 28, 3); ctx.fill() }
    // rank
    ctx.textAlign = 'center'; ctx.font = '800 30px ' + MONO; ctx.fillStyle = top ? C.green : C.faint
    ctx.fillText(String(i + 1), x + 46, ry + rowH / 2 + 11)
    // name + matchup/line
    ctx.textAlign = 'left'; ctx.font = '900 36px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, Wd - 2 * PAD - 340), x + 86, ry + 46)
    const meta = `${r.pos ? r.pos + ' · ' : ''}${r.team}${r.vs ? ` · vs ${r.vs}` : ''}${r.lineLabel ? `  ·  ${r.lineLabel}` : ''}`
    ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(_ellip(ctx, meta, Wd - 2 * PAD - 340), x + 86, ry + 82)
    // value tag
    if (r.value) {
      ctx.textAlign = 'right'; ctx.font = '800 16px ' + MONO; ctx.fillStyle = C.green
      ctx.fillText('✓ VALUE', Wd - PAD - 28, ry + 34)
    }
    // hit %
    ctx.textAlign = 'right'; ctx.font = '900 52px ' + MONO; ctx.fillStyle = top ? C.green : C.text
    ctx.fillText(`${r.pct}%`, Wd - PAD - 28, ry + rowH / 2 + 24)
    ctx.textAlign = 'left'
  })
  y += 20; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Green edge = model beats the price (Value) · calibrated hit % · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the player-rankings export for one market.
export async function shareNflRankings(market, season, week, top = 16) {
  const meta = _RANK_META[market] || { title: (market || '').toUpperCase(), sub: 'model-ranked' }
  let rows = []
  try { const d = await getNFLLeaderboard(market, season, week, 60); rows = d.rows || [] } catch (_) {}
  const seen = new Set(), out = []
  for (const r of rows) {
    const nm = r.full_name; if (!nm || seen.has(nm)) continue
    seen.add(nm)
    out.push({ name: nm, pos: r.pos || '', team: r.team || '', vs: r.opp || '',
               pct: _rowPct(r), lineLabel: _lineLabel(market, r.line), value: !!r.value })
    if (out.length === top) break
  }
  if (!out.length) { alert('No ranked players to export yet for this market.'); return }
  return shareCanvas(drawNflRankings({ title: meta.title, sub: meta.sub, dateStr: _todayStr(), week, rows: out }),
    `linebrkr-nfl-${market}-rankings.png`,
    `LINEBRKR ${meta.title} rankings — model-ranked & calibrated. Free → linebrkr.app`)
}

// Sharpest Spots card — the slate's best VALUE, ranked by the composite (edge + in-week CLV +
// market beatability). Leads each row with the edge (pp) that ranks it, the one-line why, a
// SHARP/EFF market-grade tag, and a unit chip. The shareable face of the Sharpest Spots board.
const _EFF_RED = '#FF8F6B'
function drawNflSharpestSpots({ dateStr, week, rows }) {
  const Wd = 1080, PAD = 56, rowH = 128, rowGap = 12, n = rows.length
  const Hd = PAD + 44 + 34 + 74 + 30 + 24 + n * (rowH + rowGap) + 20 + 46 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.17)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 42
  ctx.textAlign = 'left'; ctx.font = '900 42px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'
  ctx.fillText('SHARPEST SPOTS', Wd - PAD, PAD + 38); ctx.textAlign = 'left'
  y += 34; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green
  ctx.fillText(`${dateStr || ''} · WEEK ${week ?? ''}`.trim(), x, y)
  y += 60; ctx.font = '900 66px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('🎯 Sharpest Spots', x, y)
  y += 30; ctx.font = '700 21px system-ui, Arial'; ctx.fillStyle = C.muted
  ctx.fillText('The slate’s best value — edge + line movement + markets we beat', x, y)
  y += 24
  rows.forEach((r, i) => {
    const ry = y; y += rowH + rowGap; const top = i < 3
    ctx.fillStyle = top ? 'rgba(0,217,126,0.08)' : C.card
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.fill()
    ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.5
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.stroke()
    // rank
    ctx.textAlign = 'center'; ctx.font = '800 30px ' + MONO; ctx.fillStyle = top ? C.green : C.faint
    ctx.fillText(String(i + 1), x + 46, ry + rowH / 2 + 8)
    // name
    ctx.textAlign = 'left'; ctx.font = '900 36px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, Wd - 2 * PAD - 360), x + 86, ry + 44)
    // market · team · best price
    ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted
    const meta = `${r.pos ? r.pos + ' · ' : ''}${r.team}  ·  ${r.bet}${r.price ? `  ·  ${r.price}` : ''}`
    ctx.fillText(_ellip(ctx, meta, Wd - 2 * PAD - 360), x + 86, ry + 78)
    // why (green, small)
    if (r.why) { ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.green
      ctx.fillText('✓ ' + _ellip(ctx, r.why, Wd - 2 * PAD - 360), x + 86, ry + 108) }
    // market-grade tag, top-right
    if (r.grade === 'SHARP' || r.grade === 'EFFICIENT') {
      ctx.textAlign = 'right'; ctx.font = '800 15px ' + MONO
      ctx.fillStyle = r.grade === 'SHARP' ? C.green : _EFF_RED
      ctx.fillText(r.grade === 'SHARP' ? '● SHARP' : 'EFFICIENT', Wd - PAD - 28, ry + 32)
    }
    // edge (pp) — the number that ranks the board
    ctx.textAlign = 'right'; ctx.font = '900 50px ' + MONO; ctx.fillStyle = top ? C.green : C.text
    ctx.fillText(`+${r.edge}`, Wd - PAD - 74, ry + rowH / 2 + 20)
    ctx.font = '800 18px ' + MONO; ctx.fillStyle = top ? C.green : C.muted
    ctx.fillText('pp', Wd - PAD - 28, ry + rowH / 2 + 20)
    // hit% under the edge
    ctx.font = '700 16px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pct}%`, Wd - PAD - 28, ry + rowH / 2 + 46)
    ctx.textAlign = 'left'
  })
  y += 20; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Edge (pp) beats the price · +CLV = market moved to us · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the week's Sharpest Spots board.
export async function shareNflSharpestSpots(season, week, n = 10) {
  let spots = []
  try { const d = await getNFLSharpestSpots(season, week, n); spots = d.spots || [] } catch (_) {}
  if (spots.length < 2) { alert('Not enough priced value plays to export yet — try again once books post lines.'); return }
  const rows = spots.slice(0, n).map(s => ({
    name: s.name, pos: s.pos || '', team: s.team || '',
    // "Over" (not "O") so the line never reads as a leading zero in the mono font.
    bet: s.market === 'Anytime TD' ? 'Anytime TD' : `${playText(s.market, s.line)}`,
    price: s.book_over_odds != null ? `${s.book_over_odds > 0 ? '+' : ''}${s.book_over_odds}` : '',
    why: (s.why || []).join(' · '), grade: s.market_grade,
    edge: s.edge_pp, pct: Math.round((s.hit_prob || 0) * 100),
  }))
  return shareCanvas(drawNflSharpestSpots({ dateStr: _todayStr(), week, rows }),
    `linebrkr-sharpest-spots-wk${week}.png`,
    `LINEBRKR — Week ${week} Sharpest Spots: the slate's best value (edge + CLV + markets we beat). Research, not advice · 21+`)
}

// Single-game plays card — headlined with the matchup (used for Thursday Night). A clean
// cross-market list of the game's top model plays: rank, player, market + line, hit%, with
// a green edge tag where the model beats the price.
function drawNflGameCard({ eyebrow, matchup, sub, dateStr, rows }) {
  const Wd = 1080, PAD = 56, rowH = 104, rowGap = 12, n = rows.length
  const Hd = PAD + 44 + 34 + 74 + 30 + 24 + n * (rowH + rowGap) + 20 + 46 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.17)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 42
  ctx.textAlign = 'left'; ctx.font = '900 42px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'
  ctx.fillText((dateStr || ''), Wd - PAD, PAD + 38); ctx.textAlign = 'left'
  y += 34; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(eyebrow, x, y)
  y += 60; ctx.font = '900 60px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(matchup, x, y)
  y += 30; ctx.font = '700 21px system-ui, Arial'; ctx.fillStyle = C.muted; ctx.fillText(sub, x, y)
  y += 24
  rows.forEach((r, i) => {
    const ry = y; y += rowH + rowGap; const top = i < 3
    ctx.fillStyle = top ? 'rgba(0,217,126,0.08)' : C.card
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.fill()
    ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.5
    roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 16); ctx.stroke()
    ctx.textAlign = 'center'; ctx.font = '800 28px ' + MONO; ctx.fillStyle = top ? C.green : C.faint
    ctx.fillText(String(i + 1), x + 44, ry + rowH / 2 + 10)
    ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, Wd - 2 * PAD - 360), x + 84, ry + 44)
    ctx.font = '700 19px ' + MONO; ctx.fillStyle = C.muted
    const meta = `${r.pos ? r.pos + ' · ' : ''}${r.team}  ·  ${r.lineLabel && r.lineLabel !== 'to score' ? r.lineLabel : `${r.market}${r.lineLabel ? ` ${r.lineLabel}` : ''}`}`
    ctx.fillText(_ellip(ctx, meta, Wd - 2 * PAD - 360), x + 84, ry + 80)
    if (r.edge != null && r.edge > 0) {
      ctx.textAlign = 'right'; ctx.font = '800 16px ' + MONO; ctx.fillStyle = C.green
      ctx.fillText(`+${r.edge}% edge`, Wd - PAD - 28, ry + 34)
    }
    ctx.textAlign = 'right'; ctx.font = '900 50px ' + MONO; ctx.fillStyle = top ? C.green : C.text
    ctx.fillText(`${r.pct}%`, Wd - PAD - 28, ry + rowH / 2 + 24)
    ctx.textAlign = 'left'
  })
  y += 20; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model hit % · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the Thursday Night game's top plays.
export async function shareNflThursday(season, week) {
  let games = []
  try { const d = await getNFLSlate(season, week); games = d.games || [] } catch (_) {}
  // The TNF game: prefer the one tagged Thursday, else the earliest kickoff (TNF opens the
  // week), else the top-graded game.
  const thu = games.filter(g => (g.day || '').toLowerCase().startsWith('thu'))
  let game = thu[0]
  if (!game && games.length) {
    game = games.slice().sort((a, b) => String(a.kickoff || '').localeCompare(String(b.kickoff || '')))[0]
  }
  if (!game) { alert('No Thursday game on the board yet — check back once the slate loads.'); return }
  const away = game.away?.abbr || '', home = game.home?.abbr || ''
  const seen = new Set(), rows = []
  for (const p of (game.topPlays || [])) {
    const k = `${p.name}·${p.market}`; if (!p.name || seen.has(k)) continue
    seen.add(k)
    const isTd = p.market === 'Anytime TD'          // (was any 'td' — mislabelled Pass TDs 'to score')
    rows.push({ name: p.name, pos: p.pos || '', team: p.team || '', market: p.market || '',
                lineLabel: isTd ? 'to score' : (p.line != null ? playText(p.market, p.line) : ''),
                pct: Math.round((p.prob || 0) * 100), edge: p.edge_pct != null ? Math.round(p.edge_pct * 10) / 10 : null })
    if (rows.length === 10) break
  }
  if (!rows.length) { alert('No plays on the Thursday game yet — check back once the week is projected.'); return }
  return shareCanvas(drawNflGameCard({
      eyebrow: '🏈 THURSDAY NIGHT · WEEK ' + (week ?? ''),
      matchup: `${away} @ ${home}`, sub: "The model's top plays for tonight",
      dateStr: _todayStr(), rows }),
    'linebrkr-nfl-thursday.png',
    `LINEBRKR — Thursday Night model plays, ${away} @ ${home}. Free → linebrkr.app`)
}

// Fetch + render the 4-market Top-10 board for a week.
export async function shareNflTop10(season, week) {
  const markets = [
    { key: 'anytime_td', title: 'ANYTIME TD', sub: 'to score' },
    { key: 'rec_yards',  title: 'REC YARDS',  sub: 'over line' },
    { key: 'rush_yards', title: 'RUSH YARDS', sub: 'over line' },
    { key: 'receptions', title: 'RECEPTIONS', sub: 'over line' },
  ]
  const cols = await Promise.all(markets.map(async mk => {
    let rows = []
    try { const d = await getNFLLeaderboard(mk.key, season, week); rows = _capNfl(d.rows || [], 10) } catch (_) {}
    return { title: mk.title, sub: mk.sub, rows }
  }))
  return shareCanvas(drawNflTop10({ dateStr: _todayStr(), columns: cols }), 'linebrkr-nfl-top10.png',
    'LINEBRKR Top 10 — model-ranked across Anytime TD, Rec Yds, Rush Yds & Receptions. Free → linebrkr.app')
}

// Merge every market's leaderboard into one per-player record — the multi-stat feed the
// conviction board and matchup card render. {gsis: {name, team, pos, vs, td, recyd, ruyd, rec, note}}.
async function _mergeNfl(season, week) {
  const wants = ['anytime_td', 'rec_yards', 'rush_yards', 'receptions']
  const boards = {}
  await Promise.all(wants.map(async k => {
    try { const d = await getNFLLeaderboard(k, season, week); boards[k] = d.rows || [] } catch (_) { boards[k] = [] }
  }))
  const by = {}
  const touch = r => {
    const g = r.gsis_id || r.full_name
    if (!by[g]) by[g] = { name: r.full_name, team: r.team || '', pos: r.pos || '', vs: r.opp || '', note: r.note || '' }
    return by[g]
  }
  ;(boards.anytime_td || []).forEach(r => { const e = touch(r); e.td = _rowPct(r) })
  ;(boards.rec_yards || []).forEach(r => { const e = touch(r); e.recyd = r.proj != null ? Math.round(r.proj) : null; e.recydPct = _rowPct(r) })
  ;(boards.rush_yards || []).forEach(r => { const e = touch(r); e.ruyd = r.proj != null ? Math.round(r.proj) : null; e.ruydPct = _rowPct(r) })
  ;(boards.receptions || []).forEach(r => { const e = touch(r); e.rec = r.proj != null ? (Math.round(r.proj * 10) / 10) : null })
  return Object.values(by)
}
// 2-per-team cap + dedupe on a merged list, keeping the input order (already ranked).
function _capMerged(list, n) {
  const tc = {}, out = []
  for (const e of list) {
    if (!e.vs) continue
    if (e.team && (tc[e.team] || 0) >= 2) continue
    tc[e.team] = (tc[e.team] || 0) + 1
    out.push(e); if (out.length === n) break
  }
  return out
}

// Small stat chip used by the conviction + matchup cards.
function _chip(ctx, px, py, cw, val, lab, lead) {
  ctx.fillStyle = lead ? 'rgba(0,217,126,0.12)' : C.card; roundRect(ctx, px, py, cw, 54, 9); ctx.fill()
  ctx.strokeStyle = lead ? 'rgba(0,217,126,0.45)' : C.line; ctx.lineWidth = 1.2; roundRect(ctx, px, py, cw, 54, 9); ctx.stroke()
  ctx.textAlign = 'center'; ctx.font = '900 21px ' + MONO; ctx.fillStyle = lead ? C.green : C.text
  ctx.fillText(val, px + cw / 2, py + 27)
  ctx.font = '800 9.5px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(lab, px + cw / 2, py + 44); ctx.textAlign = 'left'
}

// Conviction board — tiered Locked In / Strong / Floor with per-player chips.
function drawNflGameplan({ dateStr, tiers }) {
  const Wd = 1080, PAD = 52, rowH = 100, rowGap = 8, tHeadH = 44, tGap = 14, headH = 200, footH = 54
  let Hd = PAD + headH
  tiers.forEach(t => { Hd += tHeadH + t.rows.length * (rowH + rowGap) + tGap })
  Hd += footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.15, -90, 70, Wd * 0.15, -90, 1100)
  glow.addColorStop(0, 'rgba(0,217,126,0.14)'); glow.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 42
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = C.green; ctx.fillText((dateStr || '') + ' · WEEK', Wd - PAD, PAD + 38); ctx.textAlign = 'left'
  y += 62; ctx.font = '900 62px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('GAME ', x, y)
  const gw = ctx.measureText('GAME ').width; ctx.fillStyle = C.green; ctx.fillText('PLAN.', x + gw, y)
  y += 32; ctx.font = '700 18px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText("This week's skill plays, ranked by conviction · TD% · yards · catches", x, y)
  y = PAD + headH
  tiers.forEach(t => {
    ctx.textAlign = 'left'; ctx.font = '900 23px system-ui, Arial'; ctx.fillStyle = t.col; ctx.fillText(t.name, x, y + 22)
    const nw = ctx.measureText(t.name).width
    ctx.font = '700 13px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(t.desc, x + nw + 14, y + 22)
    ctx.beginPath(); ctx.moveTo(x, y + 34); ctx.lineTo(Wd - PAD, y + 34); ctx.lineWidth = 2; ctx.strokeStyle = (t.lead ? 'rgba(0,217,126,0.45)' : C.line); ctx.stroke()
    y += tHeadH
    t.rows.forEach(r => {
      const rw = Wd - 2 * PAD
      ctx.fillStyle = C.card; roundRect(ctx, x, y, rw, rowH, 12); ctx.fill()
      ctx.strokeStyle = C.line; ctx.lineWidth = 1.2; roundRect(ctx, x, y, rw, rowH, 12); ctx.stroke()
      ctx.textAlign = 'left'; ctx.font = '900 24px system-ui, Arial'; ctx.fillStyle = C.text
      const nm = _ellip(ctx, r.name, rw - 300); ctx.fillText(nm, x + 20, y + 40)
      ctx.font = '700 13px ' + MONO; ctx.fillStyle = C.muted
      ctx.fillText(`${r.pos ? r.pos + ' · ' : ''}${r.team}${r.vs ? ` · vs ${r.vs}` : ''}`, x + 20, y + 68)
      // chips: TD% (lead), primary yards, catches
      const yardVal = r.pos === 'RB' ? r.ruyd : r.recyd
      const yardLab = r.pos === 'RB' ? 'RUSH YD' : 'REC YD'
      let cxr = Wd - PAD
      if (r.rec != null) { cxr -= 66; _chip(ctx, cxr, y + rowH / 2 - 27, 66, `${r.rec}`, 'REC', false); cxr -= 9 }
      if (yardVal != null) { cxr -= 74; _chip(ctx, cxr, y + rowH / 2 - 27, 74, `${yardVal}`, yardLab, false); cxr -= 9 }
      cxr -= 68; _chip(ctx, cxr, y + rowH / 2 - 27, 68, r.td != null ? `${r.td}%` : '—', 'TD', true)
      y += rowH + rowGap
    })
    y += tGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 17px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model probabilities · ranked by conviction · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

// Matchup Spots — the best YARDAGE spots (where the model sees a receiving/rushing edge),
// with the model's plain-English "why". The NFL answer to MLB Power Spots.
function drawNflSpots({ dateStr, rows }) {
  const Wd = 1080, PAD = 52, rowH = 122, rowGap = 9, headH = 210, footH = 54
  const Hd = PAD + headH + rows.length * (rowH + rowGap) + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const gl = ctx.createRadialGradient(Wd * 0.15, -90, 70, Wd * 0.15, -90, 1150); gl.addColorStop(0, 'rgba(0,217,126,0.15)'); gl.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = C.green; ctx.fillText((dateStr || '') + ' · WEEK', Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 60px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('MATCHUP ', x, PAD + 112)
  const mw = ctx.measureText('MATCHUP ').width; ctx.fillStyle = C.green; ctx.fillText('SPOTS.', x + mw, PAD + 112)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText("Where the model sees the yards — best receiving & rushing spots", x, PAD + 150)
  ctx.fillText('with the read behind each one.', x, PAD + 174)
  let y = PAD + headH
  rows.forEach((r, idx) => {
    const rw = Wd - 2 * PAD, top = idx === 0
    ctx.fillStyle = top ? 'rgba(0,217,126,0.07)' : C.card; roundRect(ctx, x, y, rw, rowH, 13); ctx.fill()
    ctx.strokeStyle = top ? 'rgba(0,217,126,0.45)' : C.line; ctx.lineWidth = top ? 1.5 : 1.1; roundRect(ctx, x, y, rw, rowH, 13); ctx.stroke()
    ctx.fillStyle = top ? C.green : 'rgba(255,255,255,0.06)'; ctx.beginPath(); ctx.arc(x + 36, y + rowH / 2, 17, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = top ? '#04140D' : C.muted; ctx.textAlign = 'center'; ctx.font = '900 17px system-ui, Arial'; ctx.fillText(String(idx + 1), x + 36, y + rowH / 2 + 6)
    ctx.textAlign = 'left'; ctx.font = '900 25px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, rw - 360), x + 66, y + 40)
    ctx.font = '700 13.5px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pos ? r.pos + ' · ' : ''}${r.team}${r.vs ? ` · vs ${r.vs}` : ''}`, x + 66, y + 66)
    if (r.why) { ctx.font = '700 13px ' + MONO; ctx.fillStyle = '#6f8a7c'; ctx.fillText(_ellip(ctx, r.why, rw - 320), x + 66, y + 92) }
    let cxr = Wd - PAD
    cxr -= 78; _chip(ctx, cxr, y + rowH / 2 - 27, 78, r.yd != null ? `${r.yd}` : '—', r.ylab, false); cxr -= 9
    cxr -= 68; _chip(ctx, cxr, y + rowH / 2 - 27, 68, r.pct != null ? `${r.pct}%` : '—', 'MODEL', true)
    y += rowH + rowGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 14px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model probabilities · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the conviction board.
export async function shareNflGameplan(season, week) {
  let merged = await _mergeNfl(season, week)
  merged = merged.filter(e => ['RB', 'WR', 'TE'].includes(e.pos) && e.td != null)
  merged.sort((a, b) => (b.td || 0) - (a.td || 0))
  const capped = _capMerged(merged, 9)
  const tiers = [
    { name: 'LOCKED IN', desc: 'top of the board', col: C.green, lead: true, rows: capped.slice(0, 3) },
    { name: 'STRONG', desc: 'live in good spots', col: C.text, rows: capped.slice(3, 6) },
    { name: 'FLOOR PLAYS', desc: 'scoring floor', col: C.muted, rows: capped.slice(6, 9) },
  ].filter(t => t.rows.length)
  return shareCanvas(drawNflGameplan({ dateStr: _todayStr(), tiers }), 'linebrkr-nfl-gameplan.png',
    "LINEBRKR Game Plan — this week's skill plays ranked by conviction, with the model's number. Free → linebrkr.app")
}

// Per-game Game Plan card — the opponent's soft spots + the proposed props that exploit them,
// one block per offense. The shareable face of the Plan tab ("PIT @ CLE game plan").
function drawNflMatchupPlan({ dateStr, away, home, sides }) {
  const Wd = 1080, PAD = 56, rowH = 92, rowGap = 10
  const blockH = s => 46 + 40 + (s.props.length * (rowH + rowGap)) + 24   // header + chips + rows
  const Hd = PAD + 44 + 34 + 64 + 20 + sides.reduce((a, s) => a + blockH(s), 0) + 46 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 42
  ctx.textAlign = 'left'; ctx.font = '900 42px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'
  ctx.fillText('GAME PLAN', Wd - PAD, PAD + 38); ctx.textAlign = 'left'
  y += 34; ctx.font = '800 20px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(`${dateStr || ''}`.trim(), x, y)
  y += 56; ctx.font = '900 60px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(`${away} @ ${home}`, x, y)
  y += 20
  sides.forEach(s => {
    y += 42; ctx.font = '900 30px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(s.off, x, y)
    const ow = ctx.measureText(s.off).width
    ctx.font = '700 22px system-ui, Arial'; ctx.fillStyle = C.muted; ctx.fillText(` attack ${s.def}`, x + ow + 6, y)
    y += 30; ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.green
    ctx.fillText(_ellip(ctx, s.chips || '—', Wd - 2 * PAD), x, y)
    y += 12
    s.props.forEach((r, i) => {
      const ry = y; y += rowH + rowGap; const top = i < 2 && r.matched
      ctx.fillStyle = top ? 'rgba(0,217,126,0.08)' : C.card
      roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 14); ctx.fill()
      ctx.strokeStyle = top ? 'rgba(0,217,126,0.4)' : C.line; ctx.lineWidth = 1.4
      roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 14); ctx.stroke()
      ctx.textAlign = 'left'; ctx.font = '900 30px system-ui, Arial'; ctx.fillStyle = C.text
      ctx.fillText(_ellip(ctx, `${r.name}`, Wd - 2 * PAD - 320), x + 24, ry + 38)
      ctx.font = '700 18px ' + MONO; ctx.fillStyle = C.muted
      ctx.fillText(_ellip(ctx, `${r.pos} · ${r.bet}`, Wd - 2 * PAD - 320), x + 24, ry + 66)
      ctx.font = '700 15px ' + MONO; ctx.fillStyle = r.matched ? C.green : C.faint
      ctx.fillText(_ellip(ctx, `${r.matched ? '✓ ' : '· '}${r.why}`, Wd - 2 * PAD - 320), x + 24, ry + 86)
      ctx.textAlign = 'right'; ctx.font = '900 44px ' + MONO; ctx.fillStyle = top ? C.green : C.text
      ctx.fillText(`${r.pct}%`, Wd - PAD - 24, ry + rowH / 2 + 14)
      ctx.textAlign = 'left'
    })
  })
  y += 46; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Soft-spot matchups · calibrated model % · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

export async function shareNflMatchupPlan(gameId, season, week) {
  const { getNFLGamePlan } = await import('./api')
  let d = {}
  try { d = await getNFLGamePlan(gameId, season, week) } catch (_) {}
  const mkSide = (plan) => {
    if (!plan || !plan.props?.length) return null
    const chips = (plan.soft_spots || []).slice(0, 6).map(s => `#${s.rank} ${s.unit}`).join('  ·  ')
    return {
      off: plan.off, def: plan.def, chips,
      props: plan.props.slice(0, 4).map(p => ({
        name: p.name, pos: p.pos,
        bet: p.market === 'Anytime TD' ? 'Anytime TD' : `${playText(p.market, p.line)}`,
        why: p.why, matched: p.matchup_rank != null,
        pct: Math.round((p.hit_prob || 0) * 100),
      })),
    }
  }
  const sides = [mkSide(d.plans?.away), mkSide(d.plans?.home)].filter(Boolean)
  if (!sides.length) { alert('No game plan to export yet — pull the slate/lines first.'); return }
  return shareCanvas(drawNflMatchupPlan({ dateStr: _todayStr(), away: d.away, home: d.home, sides }),
    `linebrkr-gameplan-${d.away}-${d.home}.png`,
    `LINEBRKR — ${d.away} @ ${d.home} game plan: the soft spots and the props that exploit them. Research, not advice · 21+`)
}

// Fetch + render the matchup-spots (yardage) card.
export async function shareNflSpots(season, week) {
  const merged = await _mergeNfl(season, week)
  // Score each player by his best yardage spot (rec or rush hit %); keep only real yardage.
  const scored = merged.map(e => {
    const recP = e.recydPct || 0, ruP = e.ruydPct || 0
    const useRush = ruP > recP
    return { ...e, pct: useRush ? ruP : recP,
             yd: useRush ? e.ruyd : e.recyd, ylab: useRush ? 'RUSH YD' : 'REC YD',
             why: e.note }
  }).filter(e => e.yd != null && e.pct > 0)
  scored.sort((a, b) => b.pct - a.pct)
  const rows = _capMerged(scored, 6)
  const top = rows[0]
  return shareCanvas(drawNflSpots({ dateStr: _todayStr(), rows }), 'linebrkr-nfl-spots.png',
    top ? `LINEBRKR Matchup Spots — top: ${top.name} (${top.pct}% ${top.ylab.toLowerCase()}). Free → linebrkr.app`
        : 'LINEBRKR Matchup Spots — where the model sees the yards. Free → linebrkr.app')
}

// ── Pay Dirt (red-zone TD setups) card ────────────────────────────────────────
// The shareable face of the Pay Dirt board: the week's best red-zone scoring SETUPS,
// each with the model's actual chance to SCORE, the plain-English why, and the best
// price. Mirrors the in-app board — SETUP (0-100 spot quality) and SCORES (TD chance)
// are kept visibly separate, and a low-chance spot is tagged LONGSHOT so a great spot
// at long odds never reads as a lock.
const RZ_RED = '#ff5a52'
const RZ_LONGSHOT = 0.45

function drawNflPaydirt({ dateStr, week, rows }) {
  const Wd = 1080, PAD = 52, rowH = 150, rowGap = 10, headH = 212, footH = 54
  const Hd = PAD + headH + rows.length * (rowH + rowGap) + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  // red glow to match the board's Pay Dirt accent
  const gl = ctx.createRadialGradient(Wd * 0.85, -90, 70, Wd * 0.85, -90, 1200)
  gl.addColorStop(0, 'rgba(255,90,82,0.16)'); gl.addColorStop(1, 'rgba(255,90,82,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = RZ_RED; ctx.fillText(`${dateStr || ''} · WEEK ${week}`, Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 60px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('PAY ', x, PAD + 112)
  const pw = ctx.measureText('PAY ').width; ctx.fillStyle = RZ_RED; ctx.fillText('DIRT.', x + pw, PAD + 112)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('The best red-zone TD setups — role × matchup × team total,', x, PAD + 150)
  ctx.fillText('with the model’s real chance to score & the best price.', x, PAD + 174)
  // legend
  ctx.font = '800 12.5px ' + MONO
  ctx.fillStyle = RZ_RED; ctx.fillText('■ SETUP', x, PAD + 200)
  ctx.fillStyle = C.muted; ctx.fillText(' = spot quality 0–100', x + 86, PAD + 200)
  const seg = ctx.measureText(' = spot quality 0–100').width
  ctx.fillStyle = C.text; ctx.fillText('  SCORES', x + 86 + seg, PAD + 200)
  ctx.fillStyle = C.muted; ctx.fillText(' = chance he scores', x + 86 + seg + ctx.measureText('  SCORES').width, PAD + 200)
  let y = PAD + headH
  rows.forEach((r, idx) => {
    const rw = Wd - 2 * PAD, longshot = (r.pct || 0) < RZ_LONGSHOT * 100
    const hot = idx < 3 && !longshot
    ctx.globalAlpha = longshot ? 0.86 : 1
    ctx.fillStyle = hot ? 'rgba(255,90,82,0.08)' : C.card; roundRect(ctx, x, y, rw, rowH, 13); ctx.fill()
    ctx.strokeStyle = hot ? 'rgba(255,90,82,0.5)' : C.line; ctx.lineWidth = hot ? 1.6 : 1.1; roundRect(ctx, x, y, rw, rowH, 13); ctx.stroke()
    // rank bubble
    ctx.fillStyle = hot ? RZ_RED : 'rgba(255,255,255,0.06)'; ctx.beginPath(); ctx.arc(x + 38, y + 44, 18, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = hot ? '#1a0604' : C.muted; ctx.textAlign = 'center'; ctx.font = '900 18px system-ui, Arial'; ctx.fillText(String(idx + 1), x + 38, y + 50)
    // name + meta
    ctx.textAlign = 'left'; ctx.font = '900 27px system-ui, Arial'; ctx.fillStyle = C.text
    const nm = _ellip(ctx, r.name, rw - 420); ctx.fillText(nm, x + 72, y + 42)
    if (longshot) {
      const nmw = ctx.measureText(nm).width, lx = x + 72 + nmw + 14
      ctx.fillStyle = 'rgba(245,158,11,0.16)'; roundRect(ctx, lx, y + 24, 108, 26, 7); ctx.fill()
      ctx.strokeStyle = 'rgba(245,158,11,0.55)'; ctx.lineWidth = 1; roundRect(ctx, lx, y + 24, 108, 26, 7); ctx.stroke()
      ctx.fillStyle = '#F59E0B'; ctx.font = '900 13px ' + MONO; ctx.fillText('LONGSHOT', lx + 13, y + 42)
    }
    ctx.font = '700 14px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pos ? r.pos + ' · ' : ''}${r.team}${r.opp ? ` vs ${r.opp}` : ''}`, x + 72, y + 70)
    if (r.why) { ctx.font = '600 14px ' + MONO; ctx.fillStyle = '#8f7a6f'; ctx.fillText(_ellip(ctx, r.why, rw - 430), x + 72, y + 96) }
    // price + VALUE row
    if (r.odds) {
      ctx.font = '800 16px ' + MONO; ctx.fillStyle = C.text; ctx.fillText(r.odds, x + 72, y + 126)
      let bx = x + 72 + ctx.measureText(r.odds).width + 10
      if (r.book) { ctx.font = '600 13px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(`@ ${r.book}`, bx, y + 126); bx += ctx.measureText(`@ ${r.book}`).width + 12 }
      if (r.value) {
        ctx.fillStyle = C.green; roundRect(ctx, bx, y + 111, 92, 24, 6); ctx.fill()
        ctx.fillStyle = '#04140D'; ctx.font = '900 12px ' + MONO; ctx.fillText('✓ VALUE', bx + 13, y + 127)
      }
    }
    // chips: SETUP (red lead) + SCORES
    let cxr = Wd - PAD
    cxr -= 86; _chipC(ctx, cxr, y + rowH / 2 - 27, 86, `${r.pct}%`, 'SCORES', _pctCol(r.pct)); cxr -= 10
    cxr -= 86; _chipC(ctx, cxr, y + rowH / 2 - 27, 86, `${r.setup}`, 'SETUP', RZ_RED, true)
    ctx.globalAlpha = 1
    y += rowH + rowGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 15px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model · SETUP ≠ odds to score · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}

// colored stat chip (value color configurable) — SETUP/SCORES on the Pay Dirt card
function _chipC(ctx, px, py, cw, val, lab, col, lead) {
  ctx.fillStyle = lead ? 'rgba(255,90,82,0.12)' : C.card; roundRect(ctx, px, py, cw, 54, 9); ctx.fill()
  ctx.strokeStyle = lead ? 'rgba(255,90,82,0.45)' : C.line; ctx.lineWidth = 1.2; roundRect(ctx, px, py, cw, 54, 9); ctx.stroke()
  ctx.textAlign = 'center'; ctx.font = '900 22px ' + MONO; ctx.fillStyle = col
  ctx.fillText(val, px + cw / 2, py + 27)
  ctx.font = '800 9.5px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(lab, px + cw / 2, py + 44); ctx.textAlign = 'left'
}
function _pctCol(p) { return p >= 65 ? C.green : p >= 55 ? '#34D399' : p >= 45 ? '#F59E0B' : '#ff6b6b' }

// Fetch + render the Pay Dirt card.
export async function shareNflPaydirt(season, week, n = 8) {
  let spots = []
  try { const d = await getNFLRedZoneSpots(season, week, 40); spots = d.spots || [] } catch (_) {}
  if (spots.length < 2) { alert('Pay Dirt board isn’t built for this week yet — check back near kickoff, then try again.'); return }
  // Export the board as shown: ranked by SETUP (rz_score), top N.
  spots = spots.slice().sort((a, b) => (b.rz_score ?? -1) - (a.rz_score ?? -1))
  const rows = spots.slice(0, n).map(s => ({
    name: s.name, pos: s.pos || '', team: s.team || '', opp: s.opp || '',
    setup: s.rz_score, pct: Math.round((s.td_prob || 0) * 100), why: s.why || '',
    odds: s.book_odds != null ? `${s.book_odds > 0 ? '+' : ''}${s.book_odds}` : '',
    book: s.book ? bookName(s.book) : '', value: !!s.value,
  }))
  const top = rows[0]
  return shareCanvas(drawNflPaydirt({ dateStr: _todayStr(), week, rows }), `linebrkr-paydirt-wk${week}.png`,
    top ? `LINEBRKR Pay Dirt — Week ${week}'s best red-zone TD setups (top: ${top.name}). Research, not advice · 21+`
        : `LINEBRKR Pay Dirt — the week's best red-zone TD setups. Research, not advice · 21+`)
}

// ── Favorites board (overall top plays, honestly tagged) ─────────────────────
// The week's highest-probability anytime-TD plays, ranked, each tagged by WHAT drives
// it: MATCHUP (opponent is soft to the position — the spot), BALANCED (average spot,
// usage carries it), or VOLUME (tough matchup, pure opportunity). The tag is read
// straight from td_soft_rank, so a monster usage number in a tough spot never masquerades
// as a soft matchup. Mirrors the in-app Scorers board; no odds, just the model's read.
const FAV_TAGCOL = { MATCHUP: '#00D97E', BALANCED: '#5DB4E6', VOLUME: '#F59E0B' }
function _favTag(softRank) {
  if (softRank == null) return 'BALANCED'
  if (softRank <= 10) return 'MATCHUP'
  if (softRank >= 20) return 'VOLUME'
  return 'BALANCED'
}

function drawNflFavorites({ dateStr, week, rows }) {
  const Wd = 1080, PAD = 52, rowH = 118, rowGap = 9, headH = 206, footH = 54
  const Hd = PAD + headH + rows.length * (rowH + rowGap) + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const gl = ctx.createRadialGradient(Wd * 0.85, -90, 70, Wd * 0.85, -90, 1200)
  gl.addColorStop(0, 'rgba(0,217,126,0.15)'); gl.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(`${dateStr || ''} · WEEK ${week}`, Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 58px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('FAVORITES.', x, PAD + 110)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('The week’s top anytime-TD plays, model-ranked — each tagged', x, PAD + 148)
  ctx.fillText('by what drives it: the matchup, or pure volume.', x, PAD + 172)
  // legend
  ctx.font = '800 12.5px ' + MONO
  let lx = x
  for (const [lab, col] of [['■ MATCHUP', FAV_TAGCOL.MATCHUP], ['■ BALANCED', FAV_TAGCOL.BALANCED], ['■ VOLUME', FAV_TAGCOL.VOLUME]]) {
    ctx.fillStyle = col; ctx.fillText(lab, lx, PAD + 200); lx += ctx.measureText(lab).width + 26
  }
  let y = PAD + headH
  rows.forEach((r, idx) => {
    const rw = Wd - 2 * PAD, hot = idx < 3, tcol = FAV_TAGCOL[r.tag] || C.muted
    ctx.fillStyle = hot ? 'rgba(0,217,126,0.07)' : C.card; roundRect(ctx, x, y, rw, rowH, 13); ctx.fill()
    ctx.strokeStyle = hot ? 'rgba(0,217,126,0.45)' : C.line; ctx.lineWidth = hot ? 1.6 : 1.1; roundRect(ctx, x, y, rw, rowH, 13); ctx.stroke()
    // rank bubble
    ctx.fillStyle = hot ? C.green : 'rgba(255,255,255,0.06)'; ctx.beginPath(); ctx.arc(x + 38, y + rowH / 2, 18, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = hot ? '#04140D' : C.muted; ctx.textAlign = 'center'; ctx.font = '900 18px system-ui, Arial'; ctx.fillText(String(idx + 1), x + 38, y + rowH / 2 + 6)
    // name + meta
    ctx.textAlign = 'left'; ctx.font = '900 28px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, rw - 430), x + 72, y + 46)
    ctx.font = '700 14px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pos ? r.pos + ' · ' : ''}${r.team}${r.opp ? ` vs ${r.opp}` : ''}${r.soft ? ` · ${r.opp} #${r.soft} vs ${r.pos} TDs` : ''}`, x + 72, y + 78)
    // tag chip
    const tagW = 118, tagX = Wd - PAD - 108 - 10 - tagW
    ctx.fillStyle = tcol + '22'; roundRect(ctx, tagX, y + rowH / 2 - 19, tagW, 38, 9); ctx.fill()
    ctx.strokeStyle = tcol + '88'; ctx.lineWidth = 1.1; roundRect(ctx, tagX, y + rowH / 2 - 19, tagW, 38, 9); ctx.stroke()
    ctx.fillStyle = tcol; ctx.textAlign = 'center'; ctx.font = '900 14px ' + MONO; ctx.fillText(r.tag, tagX + tagW / 2, y + rowH / 2 + 5)
    // prob
    ctx.textAlign = 'right'; ctx.font = '900 34px ' + MONO; ctx.fillStyle = _pctCol(r.pct); ctx.fillText(`${r.pct}%`, Wd - PAD, y + rowH / 2 + 11)
    ctx.textAlign = 'left'
    y += rowH + rowGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 15px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model · probabilities are directional · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the Favorites card: the slate's top anytime-TD plays, honestly tagged.
export async function shareNflFavorites(season, week, n = 8) {
  let games = []
  try { const d = await getNFLScorers(season, week, 12); games = d.games || [] } catch (_) {}
  const all = games.flatMap(g => [...(g.away_scorers || []), ...(g.home_scorers || [])])
    .filter(s => s && (s.prob || 0) > 0)
  if (all.length < 3) { alert('Favorites board isn’t built for this week yet — check back near kickoff, then try again.'); return }
  all.sort((a, b) => (b.prob || 0) - (a.prob || 0))
  // one row per player, top N
  const seen = new Set(), rows = []
  for (const s of all) {
    const k = s.gsis_id || s.name
    if (seen.has(k)) continue
    seen.add(k)
    rows.push({ name: s.name, pos: (s.pos || '').toUpperCase(), team: s.team, opp: s.opp,
      pct: Math.round((s.prob || 0) * 100), soft: s.td_soft_rank, tag: _favTag(s.td_soft_rank) })
    if (rows.length >= n) break
  }
  const top = rows[0]
  return shareCanvas(drawNflFavorites({ dateStr: _todayStr(), week, rows }), `linebrkr-favorites-wk${week}.png`,
    top ? `LINEBRKR Favorites — Week ${week}'s top plays (top: ${top.name}, ${top.pct}%). Research, not advice · 21+`
        : `LINEBRKR Favorites — the week's top plays, model-ranked. Research, not advice · 21+`)
}

// ── DUE board (cold but still projected to score) ────────────────────────────
// Players the model still projects to score who have gone cold — ranked by model
// conviction, with games-since-TD and recent touches shown so a usage collapse never
// reads as "due." Positive-regression value, framed honestly (not the gambler's fallacy).
const DUE_AMBER = '#F5A524'
function drawNflDue({ dateStr, week, rows }) {
  const Wd = 1080, PAD = 52, rowH = 122, rowGap = 9, headH = 210, footH = 54
  const Hd = PAD + headH + rows.length * (rowH + rowGap) + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const gl = ctx.createRadialGradient(Wd * 0.85, -90, 70, Wd * 0.85, -90, 1200)
  gl.addColorStop(0, 'rgba(245,165,36,0.14)'); gl.addColorStop(1, 'rgba(245,165,36,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = DUE_AMBER; ctx.fillText(`${dateStr || ''} · WEEK ${week}`, Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 60px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('DUE', x, PAD + 112)
  const dw = ctx.measureText('DUE').width; ctx.fillStyle = DUE_AMBER; ctx.fillText('.', x + dw, PAD + 112)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('Still projected to score — but they’ve gone cold. The touches', x, PAD + 150)
  ctx.fillText('are there, the finishes haven’t come (not “he’s due” — value).', x, PAD + 174)
  ctx.font = '800 12.5px ' + MONO; ctx.fillStyle = DUE_AMBER; ctx.fillText('■ COLD', x, PAD + 200)
  ctx.fillStyle = C.muted; ctx.fillText(' = games since last TD, still getting touches', x + 72, PAD + 200)
  let y = PAD + headH
  rows.forEach((r, idx) => {
    const rw = Wd - 2 * PAD, hot = idx < 3
    ctx.fillStyle = hot ? 'rgba(245,165,36,0.07)' : C.card; roundRect(ctx, x, y, rw, rowH, 13); ctx.fill()
    ctx.strokeStyle = hot ? 'rgba(245,165,36,0.42)' : C.line; ctx.lineWidth = hot ? 1.6 : 1.1; roundRect(ctx, x, y, rw, rowH, 13); ctx.stroke()
    ctx.fillStyle = hot ? DUE_AMBER : 'rgba(255,255,255,0.06)'; ctx.beginPath(); ctx.arc(x + 38, y + rowH / 2, 18, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = hot ? '#1a1304' : C.muted; ctx.textAlign = 'center'; ctx.font = '900 18px system-ui, Arial'; ctx.fillText(String(idx + 1), x + 38, y + rowH / 2 + 6)
    ctx.textAlign = 'left'; ctx.font = '900 28px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, rw - 440), x + 72, y + 44)
    ctx.font = '700 14px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pos ? r.pos + ' · ' : ''}${r.team}${r.opp ? ` vs ${r.opp}` : ''} · last TD ${r.last_td} · ${r.avg_touches} touches/g`, x + 72, y + 76)
    // COLD chip (drought) + prob
    const chW = 108, chX = Wd - PAD - 92 - 10 - chW
    ctx.fillStyle = 'rgba(245,165,36,0.12)'; roundRect(ctx, chX, y + rowH / 2 - 27, chW, 54, 9); ctx.fill()
    ctx.strokeStyle = 'rgba(245,165,36,0.45)'; ctx.lineWidth = 1.2; roundRect(ctx, chX, y + rowH / 2 - 27, chW, 54, 9); ctx.stroke()
    ctx.fillStyle = DUE_AMBER; ctx.textAlign = 'center'; ctx.font = '900 22px ' + MONO; ctx.fillText(`${r.drought}g`, chX + chW / 2, y + rowH / 2)
    ctx.font = '800 9.5px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText('COLD', chX + chW / 2, y + rowH / 2 + 17)
    ctx.textAlign = 'right'; ctx.font = '900 30px ' + MONO; ctx.fillStyle = _pctCol(r.prob); ctx.fillText(`${r.prob}%`, Wd - PAD, y + rowH / 2 + 10)
    ctx.textAlign = 'left'
    y += rowH + rowGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 15px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('A cold streak doesn’t make a TD likelier — this is usage the finish hasn’t matched · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}

// Fetch + render the DUE card.
export async function shareNflDue(season, week, n = 8) {
  let due = []
  try { const d = await getNFLDueTds(season, week, Math.max(n, 12)); due = d.due || [] } catch (_) {}
  if (due.length < 2) { alert('DUE board needs a few games of history first — check back once the season banks more weeks.'); return }
  const rows = due.slice(0, n).map(r => ({
    name: r.name, pos: (r.pos || '').toUpperCase(), team: r.team, opp: r.opp,
    prob: r.prob, drought: r.drought, last_td: r.last_td, avg_touches: r.avg_touches,
  }))
  const top = rows[0]
  return shareCanvas(drawNflDue({ dateStr: _todayStr(), week, rows }), `linebrkr-due-wk${week}.png`,
    top ? `LINEBRKR DUE — Week ${week}'s cold-but-projected scorers (top: ${top.name}, ${top.drought}g cold, ${top.prob}%). Research, not advice · 21+`
        : `LINEBRKR DUE — still projected to score, gone cold. Research, not advice · 21+`)
}

// ── Matchup Sheets card (soft defenses → who's facing them) ──────────────────
function _rkCol(r) { return r == null ? C.muted : r <= 3 ? C.green : r <= 8 ? '#F5A524' : C.muted }
function drawNflSheets({ dateStr, week, groups }) {
  const Wd = 1080, PAD = 52, grpGap = 24, rowH = 60, headH = 186, footH = 54
  const grpH = g => 40 + Math.min(3, (g.sections || []).length) * (rowH + 6)
  const bodyH = groups.reduce((a, g) => a + grpH(g) + grpGap, 0)
  const Hd = PAD + headH + bodyH + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const gl = ctx.createRadialGradient(Wd * 0.85, -90, 70, Wd * 0.85, -90, 1200)
  gl.addColorStop(0, 'rgba(0,217,126,0.14)'); gl.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(`${dateStr || ''} · WEEK ${week}`, Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 56px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('MATCHUP SHEETS', x, PAD + 108)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('The softest defenses this week and who’s facing them —', x, PAD + 144)
  ctx.fillText('rank 1 = allows the most. Model = chance to score.', x, PAD + 166)
  let y = PAD + headH
  groups.forEach(g => {
    ctx.font = '900 20px system-ui, Arial'; ctx.fillStyle = C.green
    ctx.fillText((g.label || '').toUpperCase(), x, y + 24); y += 40
    ;(g.sections || []).slice(0, 3).forEach(s => {
      const rw = Wd - 2 * PAD
      ctx.fillStyle = C.card; roundRect(ctx, x, y, rw, rowH, 11); ctx.fill()
      ctx.strokeStyle = C.line; ctx.lineWidth = 1; roundRect(ctx, x, y, rw, rowH, 11); ctx.stroke()
      // defense + rank
      ctx.textAlign = 'left'; ctx.font = '900 21px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(s.defense, x + 16, y + 38)
      const dfw = ctx.measureText(s.defense).width
      ctx.font = '800 13px ' + MONO; ctx.fillStyle = _rkCol(s.rank); ctx.fillText(`#${s.rank}`, x + 16 + dfw + 10, y + 37)
      ctx.fillStyle = C.faint; ctx.font = '700 12px ' + MONO
      ctx.fillText(s.allowed != null ? `${s.allowed} ${s.unit}` : '', x + 16 + dfw + 10 + 34, y + 37)
      // top facing player
      const top = (s.facing || [])[0]
      if (top) {
        ctx.textAlign = 'right'
        ctx.font = '900 24px ' + MONO; ctx.fillStyle = _pctCol(top.prob); ctx.fillText(`${top.prob}%`, Wd - PAD - 16, y + 38)
        const pw = ctx.measureText(`${top.prob}%`).width
        ctx.font = '800 17px system-ui, Arial'; ctx.fillStyle = C.text
        ctx.fillText(_ellip(ctx, top.name, rw - dfw - 240), Wd - PAD - 16 - pw - 18, y + 38)
        ctx.textAlign = 'left'
      }
      y += rowH + 6
    })
    y += grpGap
  })
  ctx.textAlign = 'center'; ctx.font = '600 15px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Calibrated model · soft spot + who exploits it · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}
export async function shareNflSheets(season, week) {
  let d = { td: [] }
  try { d = await getNFLMatchupSheets(season, week) } catch (_) {}
  const groups = (d.td || []).filter(g => (g.sections || []).length)
  if (!groups.length) { alert('Matchup Sheets aren’t built for this week yet — check back near kickoff.'); return }
  return shareCanvas(drawNflSheets({ dateStr: _todayStr(), week, groups }), `linebrkr-sheets-wk${week}.png`,
    `LINEBRKR Matchup Sheets — Week ${week}'s softest defenses + who's facing them. Research, not advice · 21+`)
}

// ── Defense card (softest defenses per position) ─────────────────────────────
function drawNflDefense({ dateStr, week, cols }) {
  const Wd = 1080, PAD = 52, headH = 168, footH = 54, colGap = 20, rowH = 46
  const nRows = Math.max(...cols.map(c => c.rows.length))
  const bodyH = 44 + nRows * (rowH + 6)
  const Hd = PAD + headH + bodyH + footH + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const gl = ctx.createRadialGradient(Wd * 0.85, -90, 70, Wd * 0.85, -90, 1200)
  gl.addColorStop(0, 'rgba(0,217,126,0.13)'); gl.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = gl; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD
  ctx.textAlign = 'left'; ctx.font = '900 34px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, PAD + 42)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, PAD + 42)
  ctx.textAlign = 'right'; ctx.font = '800 19px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(`${dateStr || ''} · WEEK ${week}`, Wd - PAD, PAD + 38)
  ctx.textAlign = 'left'; ctx.font = '900 56px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('SOFTEST DEFENSES', x, PAD + 108)
  ctx.font = '700 17px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText('Who gives up the most this week, by position —', x, PAD + 144)
  ctx.fillText('the teams to attack. Rank 1 = allows the most.', x, PAD + 166)
  const colW = (Wd - 2 * PAD - colGap * (cols.length - 1)) / cols.length
  cols.forEach((c, ci) => {
    const cx = x + ci * (colW + colGap)
    ctx.textAlign = 'left'; ctx.font = '900 16px system-ui, Arial'; ctx.fillStyle = C.green
    ctx.fillText(c.title, cx + 4, PAD + headH + 20)
    let yy = PAD + headH + 44
    c.rows.forEach((r, ri) => {
      ctx.fillStyle = ri < 3 ? 'rgba(0,217,126,0.06)' : C.card; roundRect(ctx, cx, yy, colW, rowH, 9); ctx.fill()
      ctx.strokeStyle = C.line; ctx.lineWidth = 1; roundRect(ctx, cx, yy, colW, rowH, 9); ctx.stroke()
      ctx.textAlign = 'left'; ctx.font = '800 13px ' + MONO; ctx.fillStyle = _rkCol(ri + 1); ctx.fillText(`${ri + 1}`, cx + 12, yy + 29)
      ctx.font = '900 19px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(r.team, cx + 34, yy + 30)
      ctx.textAlign = 'right'; ctx.font = '800 15px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText(String(r.val), cx + colW - 12, yy + 30)
      ctx.textAlign = 'left'
      yy += rowH + 6
    })
  })
  ctx.textAlign = 'center'; ctx.font = '600 15px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Defense-vs-position · season to date · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}
export async function shareNflDefenseCard(season, week) {
  let rows = []
  try { const d = await getNFLDefense(season, week); rows = d.rows || [] } catch (_) {}
  if (rows.length < 4) { alert('Defense board isn’t loaded for this week yet — check back soon.'); return }
  const top = (rankKey, valKey, n = 6) => rows.filter(r => r[rankKey] != null)
    .sort((a, b) => a[rankKey] - b[rankKey]).slice(0, n).map(r => ({ team: r.team, val: r[valKey] }))
  const cols = [
    { title: 'RB TDs', rows: top('rbtr', 'rb_td') },
    { title: 'WR TDs', rows: top('wrtr', 'wr_td') },
    { title: 'TE TDs', rows: top('tetr', 'te_td') },
    { title: 'QB pass yds', rows: top('qbr', 'qb') },
  ]
  return shareCanvas(drawNflDefense({ dateStr: _todayStr(), week, cols }), `linebrkr-defense-wk${week}.png`,
    `LINEBRKR — Week ${week}'s softest defenses by position (who to attack). Research, not advice · 21+`)
}

// Fetch + render a single-market leaderboard card.
export async function shareNflMarket(market, season, week) {
  const meta = {
    anytime_td: { title: 'ANYTIME TD', sub: 'Model-ranked to find the end zone.' },
    rec_yards:  { title: 'REC YARDS',  sub: 'Top receiving-yard spots. Model-ranked.' },
    rush_yards: { title: 'RUSH YARDS', sub: 'Top rushing-yard spots. Model-ranked.' },
    receptions: { title: 'RECEPTIONS', sub: 'Most likely to clear the catch line.' },
    pass_yards: { title: 'PASS YARDS', sub: 'Top passing-yard spots. Model-ranked.' },
  }[market] || { title: (market || '').toUpperCase(), sub: 'Model-ranked.' }
  let rows = []
  try { const d = await getNFLLeaderboard(market, season, week); rows = _capNfl(d.rows || [], 6) } catch (_) {}
  const lead = rows[0]
  return shareCanvas(drawNflMarket({ ...meta, dateStr: _todayStr(), rows }), `linebrkr-nfl-${market}.png`,
    lead ? `LINEBRKR ${meta.title} — top: ${lead.name} ${lead.pct}%. Model-ranked, free → linebrkr.app`
         : `LINEBRKR ${meta.title} board — model-ranked, free → linebrkr.app`)
}

// The graded-results recap — "the board went X-Y, here's what cashed." A CASHED list of
// the hits and a MISSED list (shown honestly), off the settled featured board.
function _recapResult(p) {
  const isTD = p.market === 'Anytime TD'
  if (isTD) return p.status === 'hit' ? 'scored' : "didn't score"
  return p.actual != null ? `went ${p.actual}` : (p.status === 'hit' ? 'cleared' : 'under')
}
function drawNflRecap({ dateStr, week, record, hits, misses }) {
  const Wd = 1080, PAD = 56, rowH = 68, rowGap = 8, secHead = 42
  const headH = 300
  let Hd = PAD + headH
  Hd += secHead + hits.length * (rowH + rowGap)
  if (misses.length) Hd += 22 + secHead + misses.length * (rowH + rowGap)
  Hd += 30 + 44 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)'); ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, 700)
  const x = PAD; let y = PAD + 46
  ctx.textAlign = 'left'; ctx.font = '900 44px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'; ctx.fillText((dateStr || '') + ' · GRADED', Wd - PAD, PAD + 40); ctx.textAlign = 'left'
  y += 66; ctx.font = '900 68px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText(`WEEK ${week} `, x, y)
  const ww = ctx.measureText(`WEEK ${week} `).width; ctx.fillStyle = C.green; ctx.fillText('RECAP.', x + ww, y)
  // headline record + hit rate
  y += 30
  const rec = `${record.wins}-${record.losses}`
  ctx.font = '900 96px ' + MONO; ctx.fillStyle = C.green; ctx.fillText(rec, x, y + 78)
  const rw = ctx.measureText(rec).width
  if (record.hit_rate != null) {
    ctx.font = '900 60px ' + MONO; ctx.fillStyle = C.text; ctx.fillText(`${record.hit_rate}%`, x + rw + 30, y + 78)
    ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText('HIT RATE', x + rw + 32, y + 42)
  }
  ctx.font = '700 20px ' + MONO; ctx.fillStyle = C.muted; ctx.fillText('FEATURED PLAYS', x + 2, y + 40)
  y = PAD + headH
  const section = (title, col, list) => {
    ctx.textAlign = 'left'; ctx.font = '900 22px system-ui, Arial'; ctx.fillStyle = col; ctx.fillText(title, x, y + 22)
    ctx.beginPath(); ctx.moveTo(x, y + 34); ctx.lineTo(Wd - PAD, y + 34); ctx.lineWidth = 2; ctx.strokeStyle = `${col}55`; ctx.stroke()
    y += secHead
    list.forEach(p => {
      const rww = Wd - 2 * PAD
      ctx.fillStyle = C.card; roundRect(ctx, x, y, rww, rowH, 11); ctx.fill()
      ctx.strokeStyle = `${col}44`; ctx.lineWidth = 1; roundRect(ctx, x, y, rww, rowH, 11); ctx.stroke()
      ctx.textAlign = 'left'; ctx.font = '900 25px system-ui, Arial'; ctx.fillStyle = C.text
      ctx.fillText(`${col === C.green ? '✓' : '✕'} ${_ellip(ctx, p.name, rww - 300)}`, x + 18, y + 30)
      ctx.font = '700 13px ' + MONO; ctx.fillStyle = C.muted
      const bet = p.market === 'Anytime TD' ? 'Anytime TD' : `${playText(p.market, p.line)}`
      ctx.fillText(`${p.team || ''}${p.opp ? ` · vs ${p.opp}` : ''} · ${bet}`, x + 40, y + 54)
      ctx.textAlign = 'right'; ctx.font = '800 20px ' + MONO; ctx.fillStyle = col
      ctx.fillText(_recapResult(p), Wd - PAD - 18, y + rowH / 2 + 7)
      ctx.textAlign = 'left'
      y += rowH + rowGap
    })
  }
  section('✓ CASHED', C.green, hits)
  if (misses.length) { y += 22; section('✕ MISSED', '#ef5a52', misses) }
  ctx.textAlign = 'center'; ctx.font = '600 18px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Graded vs official box scores · research, not advice · 21+', Wd / 2, Hd - PAD - 6)
  ctx.textAlign = 'left'
  return cv
}

// Fetch the settled featured board and render the recap card.
export async function shareNflRecap(season, week) {
  let d = null
  try { d = await getNFLResults(season, week, 'featured') } catch (_) {}
  const rec = (d && d.record) || { wins: 0, losses: 0, hit_rate: null }
  const plays = (d && d.plays) || []
  const byProb = (a, b) => (b.prob || 0) - (a.prob || 0)
  const hits = plays.filter(p => p.status === 'hit').sort(byProb).slice(0, 8)
  const misses = plays.filter(p => p.status === 'miss').sort(byProb).slice(0, 5)
  if (!hits.length && !misses.length) {
    // Nothing graded yet — fall back to the proof/record card instead of an empty recap.
    return shareCanvas(drawProofCard({ eyebrow: `Week ${week}`, big: '—', bigLabel: 'not graded yet',
      context: 'Results land as games finalize' }), 'linebrkr-nfl-recap.png',
      `LINEBRKR Week ${week} — results land as games finalize. Free → linebrkr.app`)
  }
  return shareCanvas(drawNflRecap({ dateStr: _todayStr(), week, record: rec, hits, misses }), 'linebrkr-nfl-recap.png',
    `LINEBRKR Week ${week} recap — the board went ${rec.wins}-${rec.losses}${rec.hit_rate != null ? ` (${rec.hit_rate}%)` : ''}, graded vs the finals. Free → linebrkr.app`)
}

// Top-N graded results — the week's top recommended plays with HIT/MISS, ranked, for proof posts.
function drawNflTopResults({ week, n, shown, wins, losses }) {
  const RED = '#F0596A', AMBER = '#F6B33C'
  const Wd = 1080, PAD = 56, rowH = 96, rowGap = 10, rows = shown.length
  const hr = (wins + losses) ? Math.round(1000 * wins / (wins + losses)) / 10 : 0
  const Hd = PAD + 44 + 52 + 108 + 44 + 30 + rows * (rowH + rowGap) + 18 + 40 + PAD
  const cv = document.createElement('canvas'); cv.width = Wd; cv.height = Hd
  const ctx = cv.getContext('2d'); ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, Wd, Hd)
  const glow = ctx.createRadialGradient(Wd * 0.5, -80, 60, Wd * 0.5, -80, 900)
  glow.addColorStop(0, 'rgba(0,217,126,0.16)'); glow.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = glow; ctx.fillRect(0, 0, Wd, Hd)
  const x = PAD; let y = PAD + 42
  ctx.textAlign = 'left'; ctx.font = '900 42px system-ui, Arial'; ctx.fillStyle = C.text; ctx.fillText('LINE', x, y)
  const lw = ctx.measureText('LINE').width; ctx.fillStyle = C.green; ctx.fillText('BRKR', x + lw + 2, y)
  ctx.font = '800 17px ' + MONO; ctx.fillStyle = C.faint; ctx.textAlign = 'right'; ctx.fillText(`WEEK ${week} · 2026`, Wd - PAD, PAD + 38)
  // title
  y += 52; ctx.textAlign = 'center'; ctx.font = '800 24px ' + MONO; ctx.fillStyle = C.muted
  ctx.fillText(rows < n ? `ALL ${rows} RECOMMENDED PLAYS` : `TOP ${n} RECOMMENDED PLAYS`, Wd / 2, y)
  // record hero
  y += 96; ctx.font = '900 96px system-ui, Arial'
  const wS = `${wins}`, dS = '–', lS = `${losses}`
  const wW = ctx.measureText(wS).width, dW = ctx.measureText(dS).width, lW = ctx.measureText(lS).width
  let rx = Wd / 2 - (wW + dW + lW) / 2; ctx.textAlign = 'left'
  ctx.fillStyle = C.green; ctx.fillText(wS, rx, y); rx += wW
  ctx.fillStyle = C.faint; ctx.fillText(dS, rx, y); rx += dW
  ctx.fillStyle = RED; ctx.fillText(lS, rx, y)
  y += 42; ctx.textAlign = 'center'; ctx.font = '800 28px system-ui, Arial'; ctx.fillStyle = C.green
  ctx.fillText(`${hr}% hit rate · Week ${week} recommended board`, Wd / 2, y)
  ctx.textAlign = 'left'; y += 34
  shown.forEach((r, i) => {
    const ry = y; y += rowH + rowGap; const hit = r.status === 'hit'
    ctx.fillStyle = C.card; roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 14); ctx.fill()
    ctx.strokeStyle = C.line; ctx.lineWidth = 1.5; roundRect(ctx, x, ry, Wd - 2 * PAD, rowH, 14); ctx.stroke()
    ctx.textAlign = 'center'; ctx.font = '800 26px ' + MONO; ctx.fillStyle = C.faint; ctx.fillText(String(i + 1), x + 40, ry + rowH / 2 + 9)
    ctx.textAlign = 'left'; ctx.font = '900 33px system-ui, Arial'; ctx.fillStyle = C.text
    ctx.fillText(_ellip(ctx, r.name, Wd - 2 * PAD - 490), x + 76, ry + 40)
    ctx.font = '700 19px ' + MONO; ctx.fillStyle = C.muted
    const bet = r.market === 'Anytime TD' ? 'Anytime TD' : `${playText(r.market, r.line)}`
    let meta = `${r.team} · ${bet}`; ctx.fillText(_ellip(ctx, meta, Wd - 2 * PAD - 490), x + 76, ry + 72)
    if (r.proj) {
      const mw = ctx.measureText(_ellip(ctx, meta, Wd - 2 * PAD - 490)).width
      ctx.font = '800 15px ' + MONO; ctx.fillStyle = AMBER; ctx.fillText('PROJ', x + 76 + mw + 14, ry + 72)
    }
    ctx.textAlign = 'right'; ctx.font = '800 30px ' + MONO; ctx.fillStyle = C.muted
    ctx.fillText(`${r.pct}%`, Wd - PAD - 196, ry + rowH / 2 + 11)
    const bw = 150, bh = 46, bx = Wd - PAD - bw - 16, by = ry + rowH / 2 - bh / 2
    ctx.fillStyle = hit ? 'rgba(0,217,126,0.16)' : 'rgba(240,89,106,0.16)'; roundRect(ctx, bx, by, bw, bh, 10); ctx.fill()
    ctx.textAlign = 'center'; ctx.font = '900 24px system-ui, Arial'; ctx.fillStyle = hit ? C.green : RED
    ctx.fillText(hit ? '✓ HIT' : '✕ MISS', bx + bw / 2, by + bh / 2 + 8); ctx.textAlign = 'left'
  })
  y += 16; ctx.textAlign = 'center'; ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
  ctx.fillText('Recommended plays · graded at the shown line · research, not advice · 21+', Wd / 2, Hd - PAD - 4)
  ctx.textAlign = 'left'
  return cv
}

export async function shareNflTopResults(season, week, n = 10) {
  let d = null
  try { d = await getNFLResults(season, week, 'featured') } catch (_) {}
  const plays = ((d && d.plays) || [])
    .filter(p => p.recommended && (p.status === 'hit' || p.status === 'miss'))
    .sort((a, b) => (b.prob || 0) - (a.prob || 0))
  if (!plays.length) { alert('No graded recommended plays to export for this week yet.'); return }
  const shown = plays.slice(0, n).map(p => ({
    name: p.name, team: p.team, market: p.market, line: p.line,
    pct: Math.round((p.prob || 0) * 1000) / 10, status: p.status,
    proj: p.market !== 'Anytime TD' && p.line_source === 'projected',
  }))
  const wins = shown.filter(p => p.status === 'hit').length, losses = shown.length - wins
  return shareCanvas(drawNflTopResults({ week, n, shown, wins, losses }),
    `linebrkr-week${week}-top${n}.png`,
    `LINEBRKR Week ${week} — top ${shown.length} recommended went ${wins}-${losses}. Free → linebrkr.app`)
}


// Check my slip → a shareable read of the user's own ticket: each graded leg with the model's %,
// the matchup and price verdicts, and the whole-ticket chance vs what the payout implies. Watermarked
// so a shared slip brings people back to run their own. Research only.
export function shareSlip(res, labels = {}) {
  const legs = (res?.legs || []).filter(l => l.status === 'ok').slice(0, 8)
  if (!legs.length) return Promise.resolve(false)
  const pct = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
  const rows = legs.map(l => ({
    name: l.player,
    meta: [l.prop_type === 'anytime_td' ? 'Anytime TD' : playText(l.market, l.line, l.side),
           labels.matchup?.(l), labels.price?.(l)].filter(Boolean).join(' · '),
    value: pct(l.prob),
    sub: l.book_odds != null ? `book ${l.book_odds > 0 ? '+' : ''}${l.book_odds}` : 'model',
    hot: l.verdict === 'value',
  }))
  const tk = res.ticket_prob != null && legs.length > 1
    ? `Whole ticket: ${pct(res.ticket_prob)} model${res.payout_prob != null ? ` vs ${pct(res.payout_prob)} payout` : ''}${labels.ticket ? ` · ${labels.ticket}` : ''}`
    : 'Every leg vs our model and the book'
  const cv = drawStudioCard({ brandKicker: 'CHECK MY SLIP', eyebrow: `My slip · ${legs.length} leg${legs.length === 1 ? '' : 's'}`,
    title: 'Slip, graded', subtitle: tk, rows,
    footer: 'Check yours free at linebrkr.app · research, not advice · 21+ · 1-800-GAMBLER' })
  return shareCanvas(cv, 'linebrkr-slip-check.png',
    'My slip, graded by LINEBRKR: matchup + price on every leg. Check yours free → linebrkr.app · 21+')
}
